import { StackWithDividers } from '@components'

import { Divider, useModal } from '@billy/design-system'
import { type ReactElement, useCallback, useState } from 'react'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { useComponentId, useFormContext } from '../../../../../../../../../../hooks'
import type { LegalMentionsSettings } from '../../../../../../../../types/legalSettings/legalMentionsSettings'
import { LegalMentionsType } from '../../../../../../enums/legalMentionsType'
import type { ComplianceSettingsFormSchema } from '../../../../../../types/complianceSettingsFormSchema'
import { getLegalMentionsModalDefaultValues } from '../../../../../../utils/getLegalMentionsModalDefaultValues'
import { LegalMentionsModal } from '../../../../../LegalMentionsModal'
import { LegalMentionRow } from './elements/LegalMentionRow'

const legalMentionsTypes = Object.values(LegalMentionsType)

export const LegalMentions = (): ReactElement => {
  const { getValues, setValue } = useFormContext<ComplianceSettingsFormSchema>()
  const [editedType, setEditedType] = useState(LegalMentionsType.LatePaymentPenaltyRate)
  // Kept in state, not derived on render: the modal resets its form whenever this reference changes.
  const [modalDefaultValues, setModalDefaultValues] = useState(() =>
    getLegalMentionsModalDefaultValues(getValues('legalMentions')),
  )
  const legalMentionsModalId = useComponentId(ModalId.LegalMentionsModal)
  const { close: closeLegalMentionsModal, open: openLegalMentionsModal } = useModal(legalMentionsModalId)

  const handleLegalMentionEdit = useCallback(
    (legalMentionsType: LegalMentionsType) => {
      setEditedType(legalMentionsType)
      setModalDefaultValues(getLegalMentionsModalDefaultValues(getValues('legalMentions')))
      openLegalMentionsModal()
    },
    [getValues, openLegalMentionsModal],
  )

  const handleLegalMentionsModalSubmit = useCallback(
    (legalMentionsDetails: LegalMentionsSettings) => {
      setValue(`legalMentions.custom.${editedType}`, legalMentionsDetails.custom[editedType] ?? null, {
        shouldDirty: true,
      })
      closeLegalMentionsModal()
    },
    [closeLegalMentionsModal, editedType, setValue],
  )

  return (
    <>
      <StackWithDividers divider={<Divider />}>
        {legalMentionsTypes.map((legalMentionsType) => (
          <LegalMentionRow
            key={legalMentionsType}
            legalMentionsType={legalMentionsType}
            onEdit={handleLegalMentionEdit}
          />
        ))}
      </StackWithDividers>
      <LegalMentionsModal
        defaultValues={modalDefaultValues}
        id={legalMentionsModalId}
        onSubmit={handleLegalMentionsModalSubmit}
        variant={editedType}
      />
    </>
  )
}
